import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { RouteV2Id } from 'routes/routesConfigV2';
import generateDefaultProposalName from 'global/utils/generateDefaultProposalName';
import { SunLifeProposalFormDelegate } from '../../../../modules/initiate-proposal/sunLife/proposalFormDelegate';

interface DomainParams {
  groupId: string;
}

export default class SunLifeInitiateProposalPageStore {
  /** Rate segment for Sunlife is currently available only for group */
  private readonly _rateSegment: RateSegment = 'GROUP';
  private _isActionLoading = false;
  public initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public domainParams: DomainParams;
  public proposalFormDelegate: SunLifeProposalFormDelegate;

  public get proposalForm(): SunLifeProposalFormDelegate['proposalForm'] {
    return this.proposalFormDelegate.proposalForm;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canCompleteStep(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this._isActionLoading &&
      !this.initiateQuoteFlowDelegate.isInitialDataLoading
    );
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    this.initiateQuoteFlowDelegate.setIsInitialDataLoading(true);

    await this.initiateQuoteFlowDelegate.getFlowInitialData();
    runInAction(() => {
      this.proposalFormDelegate.proposalForm.populateFields({
        proposalName: generateDefaultProposalName(this.initiateQuoteFlowDelegate.groupModule.groupName),
      });
      this.initiateQuoteFlowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async saveProposal(): Promise<void> {
    this._setActionLoading(true);

    const { effectiveDate, zip, state, county, sic4, groupSize, proposalName } =
      this.proposalForm.getFormDataObject() as Record<string, string>;

    await this.initiateQuoteFlowDelegate.createCartAndSaveProposal({
      effectiveDate,
      proposalName,
      rateSegment: this._rateSegment,
      browsingCriteria: {
        effectiveDate,
        zipCode: zip,
        state,
        county,
        sic4,
        groupSize,
        shortName: {},
      },
    });
    runInAction(() => {
      this._setActionLoading(false);
      this.initiateQuoteFlowDelegate.onCompleteRedirect();
    });
  }

  constructor(params: DomainParams) {
    this.domainParams = params;
    this.initiateQuoteFlowDelegate = new InitiateQuoteFlowDelegate({
      domainParams: { groupId: params.groupId, nextPageId: RouteV2Id.QUOTE_CHECKLIST },
    });

    this.proposalFormDelegate = new SunLifeProposalFormDelegate({
      rateSegment: this._rateSegment,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const SunLifeInitiateProposalPageStoreContext = createContext(
  new SunLifeInitiateProposalPageStore({
    groupId: '',
  }),
);

export const useSunLifeInitiateProposalPageStore = (): SunLifeInitiateProposalPageStore => {
  return useContext(SunLifeInitiateProposalPageStoreContext);
};

export { SunLifeInitiateProposalPageStoreContext };
